import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Star, Flag, Pencil, Trash2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { toast } from "sonner";

/**
 * ReviewsBlock — native Bud Link reviews for a shop OR strain. Same shape
 * either way; the parent passes `kind="shops"` or `kind="strains"` and a
 * `slug`. Requires a signed-in consumer WITH a nickname set before they
 * can post — the UsernamePrompt handles nickname setup elsewhere.
 */

const StarRow = ({ value = 0, size = 14, interactive, onChange }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role={interactive ? "radiogroup" : undefined}
      aria-label={`${value} out of 5 stars`}
    >
      {stars.map((i) => (
        <button
          key={i}
          type="button"
          onClick={interactive ? () => onChange?.(i) : undefined}
          disabled={!interactive}
          data-testid={interactive ? `review-star-${i}` : undefined}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          aria-label={`${i} star${i === 1 ? "" : "s"}`}
        >
          <Star
            width={size}
            height={size}
            className={
              i <= value
                ? "fill-[#f0b938] text-[#f0b938]"
                : "text-foreground/25 hover:text-[#f0b938]/60"
            }
          />
        </button>
      ))}
    </span>
  );
};

export const ReviewsBlock = ({ kind, slug, testId }) => {
  const { user, promptSignIn } = useConsumerAuth();
  const [data, setData] = useState({ count: 0, avg_rating: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | reviewId

  const refresh = () => {
    setLoading(true);
    api
      .get(`/reviews/${kind}/${slug}`)
      .then((r) => setData(r.data || { count: 0, avg_rating: 0, results: [] }))
      .catch(() => setData({ count: 0, avg_rating: 0, results: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (slug) refresh();
  }, [slug, kind]);

  const myReview =
    user && data.results.find((r) => r.username === user.username);

  const flag = async (rev) => {
    if (!user) return promptSignIn?.();
    if (!window.confirm("Report this review as inappropriate?")) return;
    try {
      await api.post(`/reviews/${kind}/${slug}/${rev.id}/flag`);
      toast.success("Thanks — our team will review it");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (rev) => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await api.delete(`/reviews/${kind}/${slug}/${rev.id}`);
      toast.success("Review deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section
      data-testid={testId || `reviews-${kind}-${slug}`}
      className="border border-border bg-card rounded-3xl p-6 sm:p-8"
    >
      <header className="flex flex-wrap items-center gap-3 justify-between mb-5">
        <div>
          <div className="eyebrow mb-1">Bud Link community</div>
          {data.count > 0 ? (
            <div className="flex items-center gap-3">
              <span className="font-serif text-2xl">
                {Number(data.avg_rating || 0).toFixed(1)}
              </span>
              <StarRow value={Math.round(data.avg_rating)} size={16} />
              <span className="text-sm text-foreground/60">
                {data.count} review{data.count === 1 ? "" : "s"}
              </span>
            </div>
          ) : (
            <div className="font-serif text-xl">
              Be the first to review this {kind === "shops" ? "shop" : "strain"}.
            </div>
          )}
        </div>
        {!myReview && editing !== "new" && (
          <Button
            data-testid="review-write"
            onClick={() => {
              if (!user) return promptSignIn?.();
              if (!user.username) {
                toast.error("Pick a nickname first");
                return;
              }
              setEditing("new");
            }}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-5"
          >
            Write a review
          </Button>
        )}
      </header>

      {editing === "new" && (
        <ReviewEditor
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
          kind={kind}
          slug={slug}
        />
      )}

      {loading ? (
        <p className="text-sm text-foreground/50">Loading reviews…</p>
      ) : data.results.length === 0 ? (
        <p className="text-sm text-foreground/50">
          No community reviews yet. Yours will be the first.
        </p>
      ) : (
        <div className="space-y-5">
          {data.results.map((r) =>
            editing === r.id ? (
              <ReviewEditor
                key={r.id}
                initial={r}
                onCancel={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  refresh();
                }}
                kind={kind}
                slug={slug}
              />
            ) : (
              <article
                key={r.id}
                data-testid={`review-${r.id}`}
                className="border-t border-border/60 pt-5 first:border-t-0 first:pt-0"
              >
                <header className="flex items-center gap-2 flex-wrap mb-1.5 text-xs">
                  <StarRow value={r.rating} size={13} />
                  <span className="text-foreground/85 font-medium">@{r.username}</span>
                  {r.would_recommend && (
                    <span className="pill text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                      Recommends
                    </span>
                  )}
                </header>
                {r.title && (
                  <h4 className="font-serif text-lg leading-snug mb-1">{r.title}</h4>
                )}
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                  {r.body}
                </p>
                <footer className="mt-2 flex gap-4 text-xs">
                  {user && user.username === r.username ? (
                    <>
                      <button
                        onClick={() => setEditing(r.id)}
                        data-testid={`review-edit-${r.id}`}
                        className="text-foreground/60 hover:text-foreground inline-flex items-center gap-1"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button
                        onClick={() => del(r)}
                        data-testid={`review-delete-${r.id}`}
                        className="text-destructive/80 hover:text-destructive inline-flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => flag(r)}
                      data-testid={`review-flag-${r.id}`}
                      className="text-foreground/50 hover:text-foreground inline-flex items-center gap-1"
                    >
                      <Flag className="w-3 h-3" /> Report
                    </button>
                  )}
                </footer>
              </article>
            )
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-6 flex items-start gap-1.5">
        <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
        Reviews are user-generated. Bud Link removes reviews that include
        phone numbers, emails, or off-platform sales solicitation.
      </p>
    </section>
  );
};

const ReviewEditor = ({ initial, kind, slug, onCancel, onSaved }) => {
  const [rating, setRating] = useState(initial?.rating || 5);
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  const [wouldRec, setWouldRec] = useState(
    initial?.would_recommend === false ? false : true
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      toast.error("Add a few words about your visit");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/reviews/${kind}/${slug}`, {
        rating,
        title: title.trim(),
        body: body.trim(),
        would_recommend: wouldRec,
      });
      toast.success(initial ? "Review updated" : "Review posted");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="review-editor"
      className="border border-border rounded-2xl bg-background/50 p-5 space-y-3 mb-5"
    >
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-foreground/60">Rating</span>
        <StarRow value={rating} size={22} interactive onChange={setRating} />
      </div>
      <Input
        data-testid="review-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Headline (optional)"
        maxLength={120}
        className="h-11"
      />
      <Textarea
        data-testid="review-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={
          kind === "shops"
            ? "How was the shop? Selection, staff, wait, quality…"
            : "How was this strain? Effects, taste, potency…"
        }
        rows={5}
        maxLength={2000}
        className="min-h-[120px]"
      />
      <label className="flex items-center gap-2 text-sm text-foreground/80">
        <input
          type="checkbox"
          data-testid="review-recommend"
          checked={wouldRec}
          onChange={(e) => setWouldRec(e.target.checked)}
        />
        I&apos;d recommend this to others
      </label>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="rounded-full h-10"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          data-testid="review-submit"
          disabled={busy}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6"
        >
          {busy ? "Posting…" : initial ? "Save changes" : "Post review"}
        </Button>
      </div>
    </form>
  );
};

export default ReviewsBlock;
